import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler } from "../../../helpers/handlers";

const DEFAULT_LIMIT = 25;

const initialState = {
  data: [],
  schools: [],
  lastDocId: null,
  hasMore: false,
  type: "programme",
  pageLoading: false,
  error: null,
};

const slice = createSlice({
  name: "aiCompleteness",
  initialState,
  reducers: {
    clearCompleteness: (state) => {
      state.data = [];
      state.lastDocId = null;
      state.hasMore = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompleteness.pending, (state, action) => {
        state.pageLoading = true;
        state.error = null;
        if (!action.meta.arg?.lastDocId) {
          state.data = [];
          state.lastDocId = null;
          state.hasMore = false;
        }
      })
      .addCase(fetchCompleteness.fulfilled, (state, action) => {
        const {
          data,
          lastDocId,
          hasMore,
          reset,
          type,
          schools,
        } = action.payload;
        state.type = type;
        if (Array.isArray(schools) && schools.length) {
          state.schools = schools;
        }
        if (reset) {
          state.data = [];
        }
        const incoming = data || [];
        state.data = [
          ...state.data.filter(
            (existing) => !incoming.some((item) => item.id === existing.id)
          ),
          ...incoming,
        ];
        state.lastDocId = lastDocId || null;
        state.hasMore = Boolean(hasMore);
        state.pageLoading = false;
      })
      .addCase(fetchCompleteness.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCompleteness } = slice.actions;

export const fetchCompleteness = createAsyncThunk(
  "aiCompleteness/fetch",
  async (
    {
      type = "programme",
      max = 1,
      limit = DEFAULT_LIMIT,
      lastDocId = "",
      schoolId = "",
      country = "",
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        type,
        max: String(max),
        limit: String(limit),
      });
      if (lastDocId) params.set("lastDocId", lastDocId);
      if (schoolId) params.set("schoolId", schoolId);
      if (country) params.set("country", country);
      const response = await api.get(
        `/admin/ai/data-completeness?${params.toString()}`
      );
      const payload = response.data.payload || {};
      const data = payload.data || [];
      return {
        data,
        lastDocId: payload.lastDocId || null,
        hasMore:
          typeof payload.hasMore === "boolean"
            ? payload.hasMore
            : data.length === limit,
        reset: !lastDocId,
        type,
        schools: payload.schools || [],
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export default slice.reducer;
